'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient, ApiClientError, subscribeGmailStatusListener } from '@/lib/api-client';
import { getBannerStateForErrorCode } from '@/lib/gmail-status-copy';
import { createClient } from '@/lib/supabase/client';
import { getBrowserSession, isSessionUsable } from '@/lib/supabase/session-client';
import type { Session } from '@supabase/supabase-js';
import type { GmailStatusController, GmailStatusState } from './use-gmail-status.types';

const priority: Record<GmailStatusState, number> = {
  ok: 0,
  service_unavailable: 1,
  reconnect_required: 2,
};

function stateFromStatus(value: unknown): GmailStatusState {
  if (value === 'ok' || value === 'reconnect_required' || value === 'service_unavailable') {
    return value;
  }
  return 'service_unavailable';
}

function codeFromState(state: GmailStatusState): string | null {
  if (state === 'reconnect_required') return 'GMAIL_TOKEN_EXPIRED';
  if (state === 'service_unavailable') return 'GMAIL_SERVICE_UNAVAILABLE';
  return null;
}

export function useGmailStatusController(): GmailStatusController {
  const [state, setState] = useState<GmailStatusState>('ok');
  const [lastErrorCode, setLastErrorCode] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const lastRetryRef = useRef<(() => Promise<unknown>) | null>(null);

  const applyState = useCallback((nextState: GmailStatusState, code: string | null, retry?: () => Promise<unknown>) => {
    if (retry) lastRetryRef.current = retry;

    setState((current) => {
      const resolved = nextState === 'ok' || priority[nextState] >= priority[current] ? nextState : current;
      setLastErrorCode((currentCode) => {
        if (resolved === 'ok') return null;
        if (resolved !== nextState) return currentCode ?? codeFromState(resolved);
        return code ?? codeFromState(resolved);
      });
      setIsDismissed(false);
      return resolved;
    });
  }, []);

  const reportError = useCallback((error: unknown, retry?: () => Promise<unknown>) => {
    if (!(error instanceof ApiClientError)) return;

    const nextState = getBannerStateForErrorCode(error.code);
    if (!nextState) return;

    applyState(nextState, error.code ?? codeFromState(nextState), retry);
  }, [applyState]);

  const reportHealthy = useCallback(() => {
    lastRetryRef.current = null;
    applyState('ok', null);
  }, [applyState]);

  const refresh = useCallback(async () => {
    try {
      const status = await apiClient.auth.getGmailOauthStatus();
      const nextState = stateFromStatus(status?.serviceState);
      applyState(nextState, codeFromState(nextState));
    } catch (error) {
      reportError(error);
    }
  }, [applyState, reportError]);

  const retryLastFailure = useCallback(async () => {
    try {
      const retry = lastRetryRef.current;
      if (retry) {
        await retry();
      }
      await refresh();
    } catch (error) {
      reportError(error);
    }
  }, [refresh, reportError]);

  const startReconnect = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.href = '/api/auth/google?intent=reconnect';
    }
  }, []);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  useEffect(() => subscribeGmailStatusListener(({ error, retry }) => {
    reportError(error, retry);
  }), [reportError]);

  useEffect(() => {
    let cancelled = false;
    const supabase = createClient();

    const applyAuthState = (session: Session | null) => {
      if (cancelled) return;
      const authenticated = isSessionUsable(session);
      setIsAuthenticated(authenticated);
      if (!authenticated) reportHealthy();
    };

    void getBrowserSession(supabase).then(applyAuthState).catch(() => applyAuthState(null));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      applyAuthState(session);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [reportHealthy]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void refresh();
  }, [isAuthenticated, refresh]);

  return {
    state,
    lastErrorCode,
    isDismissed,
    reportError,
    reportHealthy,
    retryLastFailure,
    startReconnect,
    dismiss,
    refresh,
  };
}